import { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudListPage } from '@core/components';
import { useCrudList } from '@core/hooks/useCrudList';
import { toProperCase } from '@core/utils/string';
import {
  type Size,
  deleteSize,
  getAllSizes,
  reorderSizes,
} from '../../../api/size';
import SizeDialog from '../../components/SizeDialog';
import { useAppContext } from '../../context/AppContext';
import { formatSizePresetSummary } from '../../utils/size';
import { getVestmentTypeLabel } from '../../utils/vestment';

const Sizes = () => {
  const { refreshContext } = useAppContext();
  const {
    items: sizes,
    loading,
    dialogOpen,
    editingItem: editingSize,
    deleteConfirm,
    load: loadSizes,
    openCreate,
    closeDialog,
    cancelDelete,
    confirmDelete,
    getRowId,
    contextMenuItems,
    rowReorder,
  } = useCrudList<Size>({
    loadItems: getAllSizes,
    deleteItem: (size) => deleteSize(size.id),
    reorderItems: reorderSizes,
    loadErrorMessage: 'Failed to load sizes',
    deleteSuccessMessage: 'Size deleted',
    deleteErrorMessage: 'Failed to delete size',
    reorderErrorMessage: 'Failed to reorder sizes',
    refreshContext,
  });

  const columns = useMemo<GridColDef<Size>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'vestment',
        headerName: 'Vestment',
        flex: 1,
        minWidth: 200,
        valueGetter: (_, row) =>
          row.vestmentType
            ? getVestmentTypeLabel(row.vestmentType)
            : toProperCase(row.vestmentCategory),
      },
      {
        field: 'measurements',
        headerName: 'Measurements',
        flex: 2,
        minWidth: 240,
        sortable: false,
        valueGetter: (_, row) => formatSizePresetSummary(row),
      },
    ],
    []
  );

  return (
    <CrudListPage<Size>
      variant="section"
      title="Sizes"
      rows={sizes}
      columns={columns}
      loading={loading}
      getRowId={getRowId}
      contextMenuItems={contextMenuItems}
      rowReorder={rowReorder}
      addConfig={{ label: 'Add Size', onAdd: openCreate }}
      addEditDialog={
        <SizeDialog
          open={dialogOpen}
          onClose={closeDialog}
          size={editingSize}
          onSuccess={() => loadSizes()}
        />
      }
      deleteConfig={{
        title: 'Delete size',
        item: deleteConfirm,
        getContent: (size) => `Are you sure you want to delete "${size.name}"?`,
        onConfirm: confirmDelete,
        onCancel: cancelDelete,
      }}
    />
  );
};

export default Sizes;
